import CssBaseline from '@mui/material/CssBaseline';
import {
  ThemeOptions,
  ThemeProvider,
  createTheme,
  responsiveFontSizes,
} from '@mui/material/styles';
import React, { useMemo } from 'react';
import { Provider } from 'react-redux';

import AppointmentModal from '../AppointmentModal';
import { store } from '../../store';
import { useAppSelector } from '../../store/hooks';

const getDesignTokens = (mode: string) => ({
  palette: {
    mode,
    ...(mode === 'dark'
      ? {
          primary: { main: '#34E3B0', light: '#6EEDC8', dark: '#0FB98C', contrastText: '#0F121A' },
          secondary: { main: '#F5F7FA', light: '#FFFFFF', dark: '#E4E8ED', contrastText: '#15263B' },
          background: { default: '#0F121A', paper: '#161B26' },
          text: { primary: '#F5F7FA', secondary: '#B4BECE' },
        }
      : {
          primary: {
            main: '#0BA075',
            light: '#34E3B0',
            dark: '#0A8B66',
            contrastText: '#FFFFFF',
          },
          secondary: {
            main: '#15263B',
            light: '#3A4A5E',
            dark: '#0E1A2A',
            contrastText: '#FFFFFF',
          },
          background: { default: '#FBFCFD', paper: '#FFFFFF' },
          text: { primary: '#15263B', secondary: '#5A6B7B' },
        }),
  },
  typography: {
    fontFamily: '"Poppins", system-ui, -apple-system, sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 14 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiButton: { styleOverrides: { root: { borderRadius: 999 } } },
    MuiCard: { styleOverrides: { root: { backgroundImage: 'none' } } },
  },
});

const ThemeWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const mode = useAppSelector(state => state.theme.mode);
  const theme = useMemo(
    () =>
      responsiveFontSizes(createTheme(getDesignTokens(mode) as ThemeOptions)),
    [mode]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Provider store={store}>
    <ThemeWrapper>
      {children}
      <AppointmentModal />
    </ThemeWrapper>
  </Provider>
);

export default Layout;
